import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, auth, getCollectionName } from './firebase';
import {
  collection, query, where, orderBy, onSnapshot,
  doc, setDoc, addDoc, updateDoc, serverTimestamp,
} from './firestore-compat';

const COLL_TOPICOS = 'huddle_topicos';
const COLL_ENTRADAS = 'huddle_entradas';

const P = {
  bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b',
  muted: '#64748b', primary: '#6366f1', green: '#16a34a', amber: '#b45309', red: '#dc2626',
};

const getLocalTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function addDiasISO(iso, delta) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

function fmtDataBR(iso) {
  const [y, m, d] = (iso || '').split('-');
  return y ? `${d}/${m}/${y}` : '—';
}

// ── Primitivas locais (padrão do restante do app) ───────────────────────────────
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: P.white, borderRadius: 14, width: '100%', maxWidth: wide ? 820 : 560,
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.25)',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 22px', borderBottom: `1px solid ${P.border}`,
          position: 'sticky', top: 0, background: P.white, zIndex: 1,
        }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: P.text }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: P.muted }}>✕</button>
        </div>
        <div style={{ padding: '20px 22px' }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: '0.71rem', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
const inp = {
  width: '100%', padding: '8px 11px', border: `1px solid ${P.border}`, borderRadius: 8,
  fontSize: '0.85rem', background: P.white, color: P.text, boxSizing: 'border-box', outline: 'none',
};
function Btn({ children, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? P.border : P.primary, color: '#fff', border: 'none',
      borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: '0.84rem',
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? .6 : 1,
    }}>
      {children}
    </button>
  );
}
function GhostBtn({ children, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: 'none', border: `1px solid ${P.border}`, color: P.muted,
      borderRadius: 8, padding: '7px 14px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
    }}>
      {children}
    </button>
  );
}

// ── Modal de gerenciamento dos tópicos ──────────────────────────────────────────
function GerenciarTopicosModal({ topicos, onClose }) {
  const [novoTitulo, setNovoTitulo] = useState('');
  const [novoResp, setNovoResp] = useState('');
  const [saving, setSaving] = useState(false);

  const salvarCampo = async (id, campo, valor) => {
    try { await updateDoc(doc(db, getCollectionName(COLL_TOPICOS), id), { [campo]: valor }); }
    catch (e) { alert('Erro ao salvar: ' + e.message); }
  };

  const adicionar = async () => {
    const titulo = novoTitulo.trim();
    if (!titulo) return;
    setSaving(true);
    try {
      const maxOrdem = topicos.reduce((m, t) => Math.max(m, parseInt(t.ordem, 10) || 0), 0);
      await addDoc(collection(db, getCollectionName(COLL_TOPICOS)), {
        titulo, responsavel: novoResp.trim(), ordem: String(maxOrdem + 1).padStart(3, '0'), ativo: true,
      });
      setNovoTitulo(''); setNovoResp('');
    } catch (e) { alert('Erro ao adicionar: ' + e.message); }
    setSaving(false);
  };

  return (
    <Modal title="⚙️ Gerenciar Tópicos do Huddle" onClose={onClose} wide>
      <p style={{ color: P.muted, fontSize: '0.82rem', marginBottom: 14 }}>
        Desmarque um tópico pra tirá-lo do preenchimento diário sem apagar o histórico dele. Edite o responsável clicando no campo abaixo do título.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 400, overflowY: 'auto', marginBottom: 18 }}>
        {topicos.map(t => (
          <div key={t.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', borderRadius: 8,
            border: `1px solid ${P.border}`, opacity: t.ativo === false ? .5 : 1,
          }}>
            <input type="checkbox" checked={t.ativo !== false} onChange={e => salvarCampo(t.id, 'ativo', e.target.checked)} style={{ marginTop: 3 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.82rem', color: P.text, fontWeight: 600, whiteSpace: 'pre-line' }}>{t.titulo}</div>
              <input
                defaultValue={t.responsavel || ''} placeholder="Responsável / área (opcional)"
                onBlur={e => { if (e.target.value.trim() !== (t.responsavel || '')) salvarCampo(t.id, 'responsavel', e.target.value.trim()); }}
                style={{ fontSize: '0.74rem', color: P.primary, fontWeight: 600, border: 'none', outline: 'none', width: '100%', marginTop: 3, background: 'transparent', padding: 0 }}
              />
            </div>
          </div>
        ))}
      </div>
      <div style={{ borderTop: `1px solid ${P.border}`, paddingTop: 14 }}>
        <Field label="Novo tópico">
          <input value={novoTitulo} onChange={e => setNovoTitulo(e.target.value)} placeholder="Título do tópico" style={inp} />
        </Field>
        <Field label="Responsável / área (opcional)">
          <input value={novoResp} onChange={e => setNovoResp(e.target.value)} placeholder="Ex: Captação" style={inp} />
        </Field>
        <Btn onClick={adicionar} disabled={saving || !novoTitulo.trim()}>{saving ? 'Salvando...' : '+ Adicionar tópico'}</Btn>
      </div>
    </Modal>
  );
}

// ── Linha de um tópico dentro do formulário de preenchimento ────────────────────
function LinhaFormulario({ topico, entrada, onSalvar, onToggleStatus }) {
  const salvo = entrada?.texto || '';
  const [texto, setTexto] = useState(salvo);
  useEffect(() => { setTexto(salvo); }, [salvo]);
  const dirty = texto !== salvo;
  const status = entrada?.status || 'pendente';

  return (
    <div style={{ padding: '10px 0 14px', borderBottom: `1px solid ${P.border}` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
        <div style={{ flex: 1, fontWeight: 700, fontSize: '0.84rem', color: P.text, whiteSpace: 'pre-line' }}>{topico.titulo}</div>
        {!!salvo && (
          <div style={{ display: 'flex', border: `1px solid ${P.border}`, borderRadius: 20, overflow: 'hidden', flexShrink: 0 }}>
            <button onClick={() => onToggleStatus(topico.id, 'pendente')} style={{
              padding: '3px 10px', fontSize: '0.68rem', fontWeight: 700, border: 'none', cursor: 'pointer',
              background: status === 'pendente' ? '#fef3c7' : '#fff', color: status === 'pendente' ? P.amber : P.muted,
            }}>Pendente</button>
            <button onClick={() => onToggleStatus(topico.id, 'resolvido')} style={{
              padding: '3px 10px', fontSize: '0.68rem', fontWeight: 700, border: 'none', cursor: 'pointer',
              background: status === 'resolvido' ? '#dcfce7' : '#fff', color: status === 'resolvido' ? P.green : P.muted,
            }}>Resolvido</button>
          </div>
        )}
      </div>
      <textarea
        value={texto} onChange={e => setTexto(e.target.value)}
        onBlur={() => { if (dirty) onSalvar(topico.id, texto); }}
        rows={2} placeholder="Sem atualização..."
        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${P.border}`, fontSize: '0.82rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
      />
    </div>
  );
}

// ── Modal "+ Novo" — preenche todos os tópicos de um dia como formulário ────────
function PreencherFormModal({ grupos, onClose }) {
  const [dia, setDia] = useState(getLocalTodayISO());
  const [entradasDoDia, setEntradasDoDia] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, getCollectionName(COLL_ENTRADAS)), where('dia', '==', dia));
    const unsub = onSnapshot(q, snap => {
      const map = {};
      snap.docs.forEach(d => { const v = d.data(); map[v.topicoId] = v; });
      setEntradasDoDia(map);
      setLoading(false);
    });
    return unsub;
  }, [dia]);

  const salvarTexto = async (topicoId, texto) => {
    const id = `${topicoId}__${dia}`;
    const statusAtual = entradasDoDia[topicoId]?.status || 'pendente';
    try {
      await setDoc(doc(db, getCollectionName(COLL_ENTRADAS), id), {
        topicoId, dia, texto, status: statusAtual,
        atualizadoPor: auth.currentUser?.email || 'desconhecido',
        atualizadoEm: serverTimestamp(), origem: 'app',
      });
    } catch (e) { alert('Erro ao salvar: ' + e.message); }
  };

  const alterarStatus = async (topicoId, status) => {
    try { await updateDoc(doc(db, getCollectionName(COLL_ENTRADAS), `${topicoId}__${dia}`), { status }); }
    catch (e) { alert('Erro ao atualizar status: ' + e.message); }
  };

  return (
    <Modal title="📝 Preencher Huddle" onClose={onClose} wide>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <label style={{ fontSize: '0.75rem', color: P.muted, fontWeight: 700, textTransform: 'uppercase' }}>Dia</label>
        <input type="date" value={dia} onChange={e => setDia(e.target.value)}
          style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${P.border}`, fontFamily: 'inherit', fontSize: '0.83rem' }} />
        {loading && <span style={{ fontSize: '0.75rem', color: P.muted }}>carregando...</span>}
      </div>
      <div style={{ maxHeight: '62vh', overflowY: 'auto', paddingRight: 4 }}>
        {grupos.map(g => (
          <div key={g.responsavel} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 800, color: P.primary, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6, paddingBottom: 4, borderBottom: `2px solid ${P.border}` }}>
              {g.responsavel}
            </div>
            {g.itens.map(t => (
              <LinhaFormulario key={t.id} topico={t} entrada={entradasDoDia[t.id]}
                onSalvar={salvarTexto} onToggleStatus={alterarStatus} />
            ))}
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ── Modal de edição de uma célula (tópico x dia) — campo grande + status ────────
function EditarCelulaModal({ topico, dia, entrada, onClose, onSalvarTexto, onSetStatus }) {
  const [texto, setTexto] = useState(entrada?.texto || '');
  const [saving, setSaving] = useState(false);
  const status = entrada?.status || 'pendente';

  const fechar = async () => {
    if (texto !== (entrada?.texto || '')) {
      setSaving(true);
      await onSalvarTexto(texto);
      setSaving(false);
    }
    onClose();
  };

  return (
    <Modal title={topico.titulo} onClose={fechar}>
      <div style={{ fontSize: '0.78rem', color: P.muted, marginBottom: 14 }}>{fmtDataBR(dia)}</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button onClick={() => onSetStatus('pendente')} style={{
          flex: 1, padding: '9px', borderRadius: 8, border: `1.5px solid ${status === 'pendente' ? P.amber : P.border}`,
          fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer',
          background: status === 'pendente' ? '#fef3c7' : '#fff', color: status === 'pendente' ? P.amber : P.muted,
        }}>🟠 Pendente</button>
        <button onClick={() => onSetStatus('resolvido')} style={{
          flex: 1, padding: '9px', borderRadius: 8, border: `1.5px solid ${status === 'resolvido' ? P.green : P.border}`,
          fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer',
          background: status === 'resolvido' ? '#dcfce7' : '#fff', color: status === 'resolvido' ? P.green : P.muted,
        }}>✅ Resolvido</button>
      </div>
      <textarea
        autoFocus value={texto} onChange={e => setTexto(e.target.value)}
        placeholder="Sem atualização..." rows={10}
        style={{ width: '100%', minHeight: 220, padding: 12, borderRadius: 8, border: `1px solid ${P.border}`, fontSize: '0.88rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', outline: 'none', lineHeight: 1.5 }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
        <GhostBtn onClick={onClose}>Cancelar</GhostBtn>
        <Btn onClick={fechar} disabled={saving}>{saving ? 'Salvando...' : 'Salvar e fechar'}</Btn>
      </div>
    </Modal>
  );
}

const JANELA_OPCOES = [7, 14, 30];

function fmtDataCurta(iso) {
  const [, m, d] = (iso || '').split('-');
  return `${d}/${m}`;
}
function diaSemanaCurto(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][new Date(y, m - 1, d).getDay()];
}

// ── Tela principal — grade estilo planilha: tópicos nas linhas, dias nas colunas ──
export default function Huddle() {
  const [todosTopicos, setTodosTopicos] = useState([]);
  const [loadingTopicos, setLoadingTopicos] = useState(true);
  const [dataFim, setDataFim] = useState(getLocalTodayISO());
  const [janela, setJanela] = useState(7);
  const [entradas, setEntradas] = useState({});
  const [loadingEntradas, setLoadingEntradas] = useState(true);
  const [gerenciarAberto, setGerenciarAberto] = useState(false);
  const [preencherAberto, setPreencherAberto] = useState(false);
  const [celulaModal, setCelulaModal] = useState(null); // { topicoId, dia }

  const hoje = getLocalTodayISO();
  const dias = useMemo(() => {
    const arr = [];
    for (let i = janela - 1; i >= 0; i--) arr.push(addDiasISO(dataFim, -i));
    return arr;
  }, [dataFim, janela]);
  const dataInicio = dias[0];

  useEffect(() => {
    const q = query(collection(db, getCollectionName(COLL_TOPICOS)), orderBy('ordem', 'asc'));
    const unsub = onSnapshot(q, snap => {
      setTodosTopicos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoadingTopicos(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    setLoadingEntradas(true);
    const q = query(
      collection(db, getCollectionName(COLL_ENTRADAS)),
      where('dia', '>=', dataInicio), where('dia', '<=', dataFim)
    );
    const unsub = onSnapshot(q, snap => {
      const map = {};
      snap.docs.forEach(d => {
        const v = d.data();
        map[`${v.topicoId}__${v.dia}`] = v;
      });
      setEntradas(map);
      setLoadingEntradas(false);
    });
    return unsub;
  }, [dataInicio, dataFim]);

  const topicosAtivos = useMemo(() => todosTopicos.filter(t => t.ativo !== false), [todosTopicos]);

  // Agrupa por responsável, sem reordenar — só cria uma seção nova quando o
  // responsável muda em relação ao tópico anterior (a ordem já vem agrupada).
  const grupos = useMemo(() => {
    const out = [];
    let last = null;
    topicosAtivos.forEach(t => {
      const resp = t.responsavel || 'Geral';
      if (resp !== last) { out.push({ responsavel: resp, itens: [] }); last = resp; }
      out[out.length - 1].itens.push(t);
    });
    return out;
  }, [topicosAtivos]);

  const salvarTexto = useCallback(async (topicoId, dia, texto) => {
    const anterior = entradas[`${topicoId}__${dia}`];
    try {
      await setDoc(doc(db, getCollectionName(COLL_ENTRADAS), `${topicoId}__${dia}`), {
        topicoId, dia, texto, status: anterior?.status || 'pendente',
        atualizadoPor: auth.currentUser?.email || 'desconhecido',
        atualizadoEm: serverTimestamp(),
        origem: 'app',
      });
    } catch (e) {
      alert('Erro ao salvar: ' + e.message);
    }
  }, [entradas]);

  const setStatus = async (topicoId, dia, status) => {
    try {
      await updateDoc(doc(db, getCollectionName(COLL_ENTRADAS), `${topicoId}__${dia}`), { status });
    } catch (e) {
      alert('Erro ao atualizar status: ' + e.message);
    }
  };

  const entradasComTexto = Object.values(entradas).filter(e => (e.texto || '').trim());
  const totalPendentes = entradasComTexto.filter(e => (e.status || 'pendente') === 'pendente').length;
  const totalResolvidos = entradasComTexto.length - totalPendentes;

  if (loadingTopicos) {
    return <div style={{ padding: '80px 40px', textAlign: 'center', color: P.muted }}>Carregando Huddle...</div>;
  }

  const colTopicoW = 260;
  const colDiaW = 210;

  return (
    <div style={{ background: P.bg, minHeight: '100vh', padding: '22px 24px 40px', display: 'flex', flexDirection: 'column', height: '100vh', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 14, flexShrink: 0 }}>
        <div>
          <h1 style={{ fontSize: 21, fontWeight: 800, color: P.text, margin: 0 }}>🗣️ Huddle Homecare</h1>
          <p style={{ color: P.muted, fontSize: 13, marginTop: 2 }}>
            {loadingEntradas ? 'Carregando...' : (
              <>
                {fmtDataBR(dataInicio)} — {fmtDataBR(dataFim)} · clique numa célula pra editar
                {entradasComTexto.length > 0 && <> · <span style={{ color: P.amber, fontWeight: 700 }}>🟠 {totalPendentes} pendentes</span> · <span style={{ color: P.green, fontWeight: 700 }}>✅ {totalResolvidos} resolvidos</span></>}
              </>
            )}
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <select value={janela} onChange={e => setJanela(Number(e.target.value))}
            style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${P.border}`, fontFamily: 'inherit', fontSize: '0.82rem', color: P.text, background: P.white, cursor: 'pointer' }}>
            {JANELA_OPCOES.map(n => <option key={n} value={n}>{n} dias</option>)}
          </select>
          <GhostBtn onClick={() => setDataFim(prev => addDiasISO(prev, -1))}>◀</GhostBtn>
          <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${P.border}`, fontFamily: 'inherit', fontSize: '0.85rem', color: P.text, background: P.white }} />
          <GhostBtn onClick={() => setDataFim(prev => addDiasISO(prev, 1))}>▶</GhostBtn>
          {dataFim !== hoje && <GhostBtn onClick={() => setDataFim(hoje)}>Hoje</GhostBtn>}
          <GhostBtn onClick={() => setGerenciarAberto(true)}>⚙️ Gerenciar tópicos</GhostBtn>
          <Btn onClick={() => setPreencherAberto(true)}>+ Novo preenchimento</Btn>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', border: `1px solid ${P.border}`, borderRadius: 12, background: P.white }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{
                position: 'sticky', top: 0, left: 0, zIndex: 3, width: colTopicoW, minWidth: colTopicoW,
                background: '#1e293b', color: '#fff', padding: '10px 14px', textAlign: 'left',
                fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: .5, borderRight: `1px solid ${P.border}`,
              }}>
                Tópico
              </th>
              {dias.map(d => {
                const ehHoje = d === hoje;
                return (
                  <th key={d} style={{
                    position: 'sticky', top: 0, zIndex: 2, width: colDiaW, minWidth: colDiaW,
                    background: ehHoje ? P.primary : '#334155', color: '#fff', padding: '10px 10px',
                    textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.12)',
                  }}>
                    <div style={{ fontWeight: 800, fontSize: '0.82rem' }}>{fmtDataCurta(d)}</div>
                    <div style={{ fontSize: '0.66rem', opacity: .8, textTransform: 'capitalize' }}>{ehHoje ? 'hoje' : diaSemanaCurto(d)}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {grupos.map(g => (
              <React.Fragment key={g.responsavel}>
                <tr>
                  <td colSpan={dias.length + 1} style={{
                    position: 'sticky', left: 0, background: '#eef2ff', color: P.primary,
                    fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: .5,
                    padding: '6px 14px', borderBottom: `1px solid ${P.border}`, borderTop: `1px solid ${P.border}`,
                  }}>
                    {g.responsavel}
                  </td>
                </tr>
                {g.itens.map(t => (
                  <tr key={t.id}>
                    <td style={{
                      position: 'sticky', left: 0, zIndex: 1, background: P.white, width: colTopicoW, minWidth: colTopicoW,
                      padding: '10px 14px', fontSize: '0.78rem', fontWeight: 600, color: P.text,
                      whiteSpace: 'pre-line', borderRight: `1px solid ${P.border}`, borderBottom: `1px solid ${P.border}`,
                      verticalAlign: 'top',
                    }}>
                      {t.titulo}
                    </td>
                    {dias.map(d => {
                      const key = `${t.id}__${d}`;
                      const ehHoje = d === hoje;
                      const valorSalvo = entradas[key]?.texto || '';
                      const status = entradas[key]?.status || 'pendente';
                      return (
                        <td key={d} onClick={() => setCelulaModal({ topicoId: t.id, dia: d })} style={{
                          width: colDiaW, minWidth: colDiaW, padding: 0, verticalAlign: 'top',
                          background: ehHoje ? '#eef2ff' : P.white,
                          borderRight: `1px solid ${P.border}`, borderBottom: `1px solid ${P.border}`,
                          borderLeft: valorSalvo ? `3px solid ${status === 'resolvido' ? P.green : P.amber}` : 'none',
                          cursor: 'pointer',
                        }}>
                          <div style={{ padding: '8px 10px', minHeight: 48, fontSize: '0.76rem', whiteSpace: 'pre-line', color: valorSalvo ? P.text : '#cbd5e1' }}>
                            {!!valorSalvo && (
                              <button
                                onClick={e => { e.stopPropagation(); setStatus(t.id, d, status === 'resolvido' ? 'pendente' : 'resolvido'); }}
                                title={status === 'resolvido' ? 'Marcar como pendente' : 'Marcar como resolvido'}
                                style={{
                                  float: 'right', border: `1px solid ${status === 'resolvido' ? '#bbf7d0' : '#fde68a'}`,
                                  background: status === 'resolvido' ? '#dcfce7' : '#fef3c7', borderRadius: 5,
                                  cursor: 'pointer', fontSize: 11, padding: '2px 5px', marginLeft: 6, lineHeight: 1,
                                }}
                              >
                                {status === 'resolvido' ? '✅ Resolvido' : '🟠 Pendente'}
                              </button>
                            )}
                            {valorSalvo || (ehHoje ? 'Clique para preencher...' : '—')}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {topicosAtivos.length === 0 && (
              <tr><td colSpan={dias.length + 1} style={{ textAlign: 'center', padding: '60px 20px', color: P.muted }}>
                Nenhum tópico ativo. Use "⚙️ Gerenciar tópicos" pra cadastrar o primeiro.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {gerenciarAberto && <GerenciarTopicosModal topicos={todosTopicos} onClose={() => setGerenciarAberto(false)} />}
      {preencherAberto && <PreencherFormModal grupos={grupos} onClose={() => setPreencherAberto(false)} />}
      {celulaModal && (
        <EditarCelulaModal
          topico={topicosAtivos.find(t => t.id === celulaModal.topicoId)}
          dia={celulaModal.dia}
          entrada={entradas[`${celulaModal.topicoId}__${celulaModal.dia}`]}
          onClose={() => setCelulaModal(null)}
          onSalvarTexto={texto => salvarTexto(celulaModal.topicoId, celulaModal.dia, texto)}
          onSetStatus={status => setStatus(celulaModal.topicoId, celulaModal.dia, status)}
        />
      )}
    </div>
  );
}
